import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { askSessionPath, libraryDocumentPath } from '@/constants/appRoutes';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTopics } from '../../hooks/useTopics';
import { ArrowLeft, Share2, Trash2, UserPlus, Users, X, Search, Shield, Eye, FileText, Plus, MessageSquare, File } from 'lucide-react';
import type { ProjectRole } from '../../services/api';
import { projectsApi, usersApi, documentApi, Document, ProjectChatItem, ProjectFileItem, Project, ProjectSharedWithUser, UserSearchResult, subjectRoots } from '../../services/api';
import { AddToProjectDialog } from './components/AddToProjectDialog';
import { useProjectsData } from './hooks/useProjectsData';
import ProjectsLayout from './components/ProjectsLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumbLabel } from '../../contexts/BreadcrumbContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { DocumentDetailsDialog } from './components/DocumentDetailsDialog';
import { ProjectsFilterPopover } from './components/ProjectsFilterPopover';
import { ProjectResourcesList, ProjectListItem } from './components/ProjectResourcesList';
import { useToast } from '@/hooks/use-toast';

function getInitials(name?: string | null, username?: string): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  if (username) return username.slice(0, 2).toUpperCase();
  return '?';
}

export default function ProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const docIdFromUrl = searchParams.get('doc');
  const { user: currentUser } = useAuth();
  const { setBreadcrumbLabel, setBreadcrumbSuffix } = useBreadcrumbLabel();
  const { toast } = useToast();
  const { projects, sharedProjects, loading: projectsLoading, refresh: refreshProjects } = useProjectsData();
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>('');
  const [currentUserRole, setCurrentUserRole] = useState<ProjectRole>('viewer');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [chats, setChats] = useState<ProjectChatItem[]>([]);
  const [files, setFiles] = useState<ProjectFileItem[]>([]);
  const [sharedWith, setSharedWith] = useState<ProjectSharedWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [removing, setRemoving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterItemKind, setFilterItemKind] = useState<'' | 'Document' | 'Chat' | 'PDF'>('');
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserRole, setAddUserRole] = useState<'editor' | 'viewer'>('viewer');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [sidebarDocumentId, setSidebarDocumentId] = useState<string | null>(null);
  const [sidebarDocument, setSidebarDocument] = useState<Document | null>(null);
  const [sidebarLoading, setSidebarLoading] = useState(false);

  const { topics } = useTopics();
  const rcmTopics = useMemo(() => subjectRoots(topics), [topics]);

  const labelForTagSlug = useCallback(
    (slug: string) => {
      for (const t of rcmTopics)
        for (const c of t.children ?? [])
          if (c.slug === slug) return c.label;
      return slug.replace(/-/g, ' ');
    },
    [rcmTopics]
  );

  const canManageAccess = currentUserRole === 'owner';
  const canEditDocuments = currentUserRole === 'owner' || currentUserRole === 'editor';

  useEffect(() => {
    if (id) {
      loadProject(id);
    } else {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (projectName) {
      setBreadcrumbLabel(projectName);
    } else {
      setBreadcrumbLabel(null);
    }
    return () => setBreadcrumbLabel(null);
  }, [projectName, setBreadcrumbLabel]);

  // Sync URL doc param -> sidebar (so /projects/:id?doc=xyz opens doc in sidebar)
  useEffect(() => {
    if (!id) return;
    if (docIdFromUrl) {
      setSidebarDocumentId(docIdFromUrl);
    } else {
      setSidebarDocumentId(null);
    }
  }, [id, docIdFromUrl]);

  // Breadcrumb suffix = document title when viewing doc in sidebar
  useEffect(() => {
    if (sidebarDocument?.title) {
      setBreadcrumbSuffix(sidebarDocument.title);
    } else {
      setBreadcrumbSuffix(null);
    }
    return () => setBreadcrumbSuffix(null);
  }, [sidebarDocument?.title, setBreadcrumbSuffix]);

  const openDocumentInSidebar = (docId: string) => {
    setSidebarDocumentId(docId);
    if (id) navigate(`/projects/${id}?doc=${docId}`, { replace: true });
  };

  const closeDocumentSidebar = () => {
    setSidebarDocumentId(null);
    setSidebarDocument(null);
    if (id) navigate(`/projects/${id}`, { replace: true });
  };

  const loadProject = async (pid: string) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const [project, docs, projectChats, projectFiles, shared] = await Promise.all([
        projectsApi.get(pid),
        projectsApi.getDocuments(pid),
        projectsApi.getChats(pid),
        projectsApi.getFiles(pid),
        projectsApi.getSharedWith(pid),
      ]);
      setProjectId(pid);
      setProjectName(project.name);
      setCurrentUserRole(project.current_user_role);
      setDocuments(docs);
      setChats(projectChats);
      setFiles(projectFiles);
      setSharedWith(shared);
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setLoading(false);
    }
  };

  const existingDocumentIds = useMemo(
    () => new Set(documents.map((d) => d.id)),
    [documents]
  );
  const existingChatIds = useMemo(
    () => new Set(chats.map((c) => c.id)),
    [chats]
  );
  const handleItemAddedToProject = useCallback(() => {
    if (projectId) void loadProject(projectId);
  }, [projectId]);

  const loadSharedWith = async () => {
    if (projectId == null) return;
    try {
      const list = await projectsApi.getSharedWith(projectId);
      setSharedWith(list);
    } catch (e) {
      console.error('Error loading shared-with:', e);
    }
  };

  useEffect(() => {
    if (!sidebarDocumentId) {
      setSidebarDocument(null);
      setSidebarLoading(false);
      return;
    }
    let cancelled = false;
    setSidebarLoading(true);
    documentApi
      .getDocument(sidebarDocumentId)
      .then((doc) => {
        if (!cancelled) setSidebarDocument(doc);
      })
      .catch(() => {
        if (!cancelled) setSidebarDocument(null);
      })
      .finally(() => {
        if (!cancelled) setSidebarLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sidebarDocumentId]);

  useEffect(() => {
    if (!userSearchQuery.trim()) {
      setUserSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setUserSearchLoading(true);
      setAddUserError(null);
      try {
        const results = await usersApi.search(userSearchQuery);
        setUserSearchResults(results);
      } catch (e) {
        setUserSearchResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [userSearchQuery]);

  const handleAddUserOpen = () => {
    setAddUserOpen(true);
    setUserSearchQuery('');
    setUserSearchResults([]);
    setAddUserRole('viewer');
    setAddUserError(null);
  };

  const handleAddUserClose = () => {
    setAddUserOpen(false);
    setUserSearchQuery('');
    setUserSearchResults([]);
    setAddUserError(null);
  };

  const handleAddUser = async (user: UserSearchResult) => {
    if (projectId == null) return;
    setAddUserError(null);
    try {
      await projectsApi.addSharedWith(projectId, user.id, addUserRole);
      await loadSharedWith();
      setUserSearchResults((prev) => prev.filter((u) => u.id !== user.id));
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : 'Failed to add user';
      setAddUserError(Array.isArray(message) ? message.join(', ') : String(message));
    }
  };

  const handleRemoveSharedWith = async (userId: string) => {
    if (projectId == null) return;
    try {
      await projectsApi.removeSharedWith(projectId, userId);
      await loadSharedWith();
    } catch (e) {
      console.error('Error removing user from library:', e);
    }
  };

  const handleUpdateSharedRole = async (userId: string, role: 'editor' | 'viewer') => {
    if (projectId == null) return;
    try {
      await projectsApi.updateSharedWithRole(projectId, userId, role);
      await loadSharedWith();
    } catch (e) {
      console.error('Error updating role:', e);
    }
  };

  const sharedWithIds = useMemo(() => new Set(sharedWith.map((u) => u.id)), [sharedWith]);
  const addableUsers = useMemo(() => {
    const exclude = new Set(sharedWithIds);
    if (currentUser?.id != null) exclude.add(currentUser.id);
    return userSearchResults.filter((u) => !exclude.has(u.id));
  }, [userSearchResults, sharedWithIds, currentUser?.id]);

  type ItemKind = 'Document' | 'Chat' | 'PDF';

  interface UnifiedItem {
    id: string;
    title: string;
    item_kind: ItemKind;
    updated_at?: string | null;
    created_at: string;
    topicLabel?: string;
    category?: string;
    meta_data?: Record<string, unknown>;
  }

  const allItems = useMemo<UnifiedItem[]>(() => {
    const docItems: UnifiedItem[] = documents.map((d) => ({
      id: d.id,
      title: d.title,
      item_kind: 'Document',
      updated_at: d.updated_at,
      created_at: d.created_at,
      topicLabel: d.tags?.[0]?.label,
      category: d.tags?.[0]?.slug,
      meta_data: undefined,
    }));
    const chatItems: UnifiedItem[] = chats.map((c) => ({
      id: c.id,
      title: c.title || 'New chat',
      item_kind: 'Chat',
      updated_at: c.updated_at,
      created_at: c.created_at,
    }));
    const fileItems: UnifiedItem[] = files.map((f) => ({
      id: f.id,
      title: f.file_name,
      item_kind: 'PDF',
      updated_at: null,
      created_at: f.created_at,
    }));
    return [...docItems, ...chatItems, ...fileItems].sort((a, b) => {
      const ta = (a.updated_at || a.created_at) ? new Date(a.updated_at || a.created_at).getTime() : 0;
      const tb = (b.updated_at || b.created_at) ? new Date(b.updated_at || b.created_at).getTime() : 0;
      return tb - ta;
    });
  }, [documents, chats, files]);

  const filteredItems = useMemo(() => {
    let list = allItems;
    if (filterItemKind) {
      list = list.filter((i) => i.item_kind === filterItemKind);
    }
    if (filterType) {
      list = list.filter((i) => i.item_kind === 'Document' && (i.topicLabel ?? '') === filterType);
    }
    if (filterCategory) {
      list = list.filter((i) => {
        if (i.item_kind !== 'Document') return false;
        return (i.category ?? '') === filterCategory;
      });
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((i) => {
        const title = (i.title || '').toLowerCase();
        const typeStr = (i.topicLabel ?? '').toLowerCase();
        const cat = (i.category ?? '').toLowerCase();
        return title.includes(q) || typeStr.includes(q) || cat.includes(q);
      });
    }
    return list;
  }, [allItems, filterItemKind, filterType, filterCategory, searchQuery]);

  const documentTypes = useMemo(() => {
    const set = new Set(documents.map((d) => d.tags?.[0]?.label ?? '').filter(Boolean));
    return Array.from(set).sort();
  }, [documents]);

  /** Tag filter options: value = slug, label = display */
  const tagFilterOptions = useMemo(() => {
    const bySlug = new Map<string, string>();
    for (const d of documents) {
      const slug = d.tags?.[0]?.slug;
      if (!slug) continue;
      bySlug.set(slug, labelForTagSlug(slug));
    }
    return Array.from(bySlug.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [documents, labelForTagSlug]);

  const categories = useMemo(
    () => tagFilterOptions.map((o) => o.value),
    [tagFilterOptions],
  );

  const isSelected = (itemId: string) => selected.has(itemId);
  const selectedCount = selected.size;

  const listItems: ProjectListItem[] = useMemo(
    () =>
      filteredItems.map((item) => {
        const subSlug = item.category;
        return {
          id: item.id,
          title: item.title,
          kind: item.item_kind,
          typeLabel:
            item.item_kind === 'Document' && item.topicLabel
              ? item.topicLabel
              : item.item_kind === 'Chat'
              ? 'Chat'
              : item.item_kind === 'PDF'
              ? 'PDF'
              : undefined,
          category: subSlug ? labelForTagSlug(subSlug) : item.category,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
        };
      }),
    [filteredItems, labelForTagSlug]
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelected(new Set(filteredItems.map((i) => i.id)));
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectOne = (docId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const handleRemoveFromProject = async () => {
    if (projectId === null || selectedCount === 0) return;
    setRemoving(true);
    try {
      const itemMap = new Map(allItems.map((i) => [i.id, i]));
      await Promise.all(
        Array.from(selected).map((itemId) => {
          const item = itemMap.get(itemId);
          if (!item) return Promise.resolve();
          if (item.item_kind === 'Document') return projectsApi.removeDocument(projectId, itemId);
          if (item.item_kind === 'Chat') return projectsApi.removeChat(projectId, itemId);
          if (item.item_kind === 'PDF') return projectsApi.removeFile(projectId, itemId);
          return Promise.resolve();
        })
      );
      setSelected(new Set());
      await loadProject(projectId);
    } catch (error) {
      console.error('Error removing items from project:', error);
    } finally {
      setRemoving(false);
    }
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setEditName(project.name);
    setEditDescription(project.description || '');
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditingProject(null);
    setEditName('');
    setEditDescription('');
  };

  const handleEditSave = async () => {
    if (!editingProject || !editName.trim()) return;
    try {
      await projectsApi.update(editingProject.id, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      });
      handleEditClose();
      // Refresh project data
      if (projectId === editingProject.id) {
        await loadProject(projectId);
      }
      // Refresh projects list
      await refreshProjects();
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const handleDelete = async (project: Project) => {
    if (!window.confirm(`Delete library "${project.name}"? Documents will not be deleted.`)) return;
    try {
      await projectsApi.delete(project.id);
      await refreshProjects();
      if (project.id === projectId) navigate('/projects');
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleItemDropOnProject = async (
    targetProjectId: string,
    payloads: { id: string; kind: 'Document' | 'Chat' | 'PDF' }[]
  ) => {
    try {
      for (const payload of payloads) {
        if (payload.kind === 'Document') {
          await projectsApi.addDocument(targetProjectId, payload.id);
        } else if (payload.kind === 'Chat') {
          await projectsApi.addChat(targetProjectId, payload.id);
        }
        // PDF: moving between projects is not currently supported, skip silently
      }
      if (projectId) {
        await loadProject(projectId);
      }
      await refreshProjects();
      toast({
        title: 'Added to library',
        description: `${payloads.length} item${payloads.length > 1 ? 's' : ''} added to the selected library.`,
      });
    } catch (error) {
      console.error('Error handling drag-and-drop between projects:', error);
      toast({
        title: 'Error',
        description: 'Could not add item to library.',
        variant: 'destructive',
      });
    }
  };


  if (loading && !projectName) {
    return (
      <ProjectsLayout
        projects={projects}
        loading={projectsLoading}
        onRefresh={refreshProjects}
        onCreateOpen={() => navigate('/projects', { state: { create: true } })}
        onEdit={handleEdit}
        onDelete={handleDelete}
        sharedProjects={sharedProjects}
        onItemDrop={handleItemDropOnProject}
      >
        <div className="flex justify-center items-center min-h-[320px]">
          <Spinner className="h-8 w-8" />
        </div>
      </ProjectsLayout>
    );
  }

  const documentDialogOpen = !!sidebarDocumentId;

  return (
    <>
      <DocumentDetailsDialog
        open={documentDialogOpen}
        loading={sidebarLoading}
        document={sidebarDocument}
        onOpenChange={(open) => {
          if (!open) closeDocumentSidebar();
        }}
        onOpenFull={() => {
          if (sidebarDocument) {
            navigate(libraryDocumentPath(sidebarDocument.id));
          }
        }}
        onOpenSource={
          sidebarDocument?.source_url
            ? () => window.open(sidebarDocument.source_url as string, '_blank')
            : undefined
        }
      />

      <ProjectsLayout
        projects={projects}
        loading={projectsLoading}
        onRefresh={refreshProjects}
        onCreateOpen={() => navigate('/projects', { state: { create: true } })}
        onEdit={handleEdit}
        onDelete={handleDelete}
        sharedProjects={sharedProjects}
        onItemDrop={handleItemDropOnProject}
      >
        <div className="w-full flex flex-col h-full">
          <div className="flex items-center gap-3 pl-4 pr-6 py-3 border-b border-border flex-shrink-0 bg-muted/30">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/projects')}
              className="lg:hidden gap-1.5 -ml-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <h1 className="text-xl font-bold text-foreground tracking-tight truncate min-w-0 flex-1">
              {projectName || 'Library'}
            </h1>
            <div className="flex items-center gap-2 flex-shrink-0">
              {allItems.length > 0 && (
                <>
                  <ProjectsFilterPopover
                    mode="library"
                    ariaLabel="Filter by type and category"
                    itemKind={filterItemKind}
                    onItemKindChange={(val) => {
                      setFilterItemKind(val);
                      setFilterType('');
                      setFilterCategory('');
                    }}
                    documentTypes={documentTypes}
                    categories={categories}
                    categoryLabels={Object.fromEntries(
                      tagFilterOptions.map((o) => [o.value, o.label]),
                    )}
                    filterType={filterType}
                    onFilterTypeChange={setFilterType}
                    filterCategory={filterCategory}
                    onFilterCategoryChange={setFilterCategory}
                  />
                  {canManageAccess ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 h-9 font-medium text-foreground border-border bg-background hover:bg-accent/50"
                      onClick={handleAddUserOpen}
                      aria-label={sharedWith.length === 0 ? 'Share library' : `Shared with ${sharedWith.length}`}
                    >
                      <Share2 className="h-4 w-4" />
                      Share
                      {sharedWith.length > 0 && (
                        <span className="ml-0.5 text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                          {sharedWith.length}
                        </span>
                      )}
                    </Button>
                  ) : sharedWith.length > 0 ? (
                    <div className="flex items-center gap-1.5 text-muted-foreground text-sm" title="Shared with you">
                      <Users className="h-4 w-4" />
                      <span>{currentUserRole === 'editor' ? 'Editor' : 'Viewer'}</span>
                    </div>
                  ) : null}
                </>
              )}
              <Button
                onClick={() => setAddDialogOpen(true)}
                size="sm"
                className="gap-2 h-9"
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          </div>

          {allItems.length === 0 ? (
            <div className="flex-1 flex items-center justify-center py-16">
              <div className="rounded-xl border border-dashed border-border bg-muted/30 p-12 text-center max-w-md mx-auto">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <FileText className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  This library is empty
                </h3>
                <p className="text-sm text-muted-foreground">
                  {canEditDocuments
                    ? 'Use the "+ Add" button to add documents, chats, or PDFs to this library.'
                    : 'This library doesn\'t have any items yet.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0 flex flex-col relative">
              <div
                className={cn(
                  'flex-1 min-h-0 overflow-auto',
                  canEditDocuments && selectedCount > 0 && 'pb-24 md:pb-20'
                )}
              >
                <ProjectResourcesList
                  items={listItems}
                  onRowClick={(item) => {
                    if (item.kind === 'Document') openDocumentInSidebar(item.id);
                    else if (item.kind === 'Chat') navigate(askSessionPath(item.id));
                  }}
                  selectable={canEditDocuments}
                  selectedIds={selected}
                  onToggleSelect={handleSelectOne}
                  onToggleSelectAll={handleSelectAll}
                />

                {filteredItems.length === 0 && allItems.length > 0 && (
                  <div className="py-8 text-center">
                    <p className="text-sm text-muted-foreground">
                      No items match the current filters.
                    </p>
                  </div>
                )}
              </div>

              {/* Floating selection mini-toolbar - appears when items selected */}
              {canEditDocuments && selectedCount > 0 && (
                <div
                  className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-4 pl-4 pr-6 py-3 rounded-t-xl border border-b-0 border-border bg-card shadow-[0_-4px_12px_rgba(0,0,0,0.08)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.3)] animate-in slide-in-from-bottom-4 duration-200"
                  role="toolbar"
                  aria-label="Selection actions"
                >
                  <span className="text-sm font-medium text-foreground">
                    {selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-muted-foreground hover:text-foreground"
                      onClick={() => setSelected(new Set())}
                      aria-label="Clear selection"
                    >
                      <X className="h-4 w-4" />
                      Clear
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                      onClick={handleRemoveFromProject}
                      disabled={removing}
                      aria-label="Remove selected items from library"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove from library
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </ProjectsLayout>

      {projectId && (
        <AddToProjectDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          projectId={projectId}
          existingDocumentIds={existingDocumentIds}
          existingChatIds={existingChatIds}
          onItemAdded={handleItemAddedToProject}
        />
      )}

      <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Manage shared access
            </DialogTitle>
            <DialogDescription>
              Control who can view and edit this library
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-2">
            {/* Currently shared section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-foreground">Currently shared with</h3>
                {sharedWith.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {sharedWith.length}
                  </Badge>
                )}
              </div>
              
              {sharedWith.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
                  <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                  <p className="text-sm text-muted-foreground font-medium mb-1">
                    Only you have access
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Add people below to share this library
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sharedWith.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors group"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Avatar className="h-10 w-10 ring-2 ring-background">
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                            {getInitials(u.full_name, u.username)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {u.full_name || u.username}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={u.role}
                          onValueChange={(val) => handleUpdateSharedRole(u.id, val as 'editor' | 'viewer')}
                        >
                          <SelectTrigger className="w-[110px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="viewer">
                              <div className="flex items-center gap-2">
                                <Eye className="h-3.5 w-3.5" />
                                Viewer
                              </div>
                            </SelectItem>
                            <SelectItem value="editor">
                              <div className="flex items-center gap-2">
                                <Shield className="h-3.5 w-3.5" />
                                Editor
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveSharedWith(u.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Add people section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-foreground">Add people</h3>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label htmlFor="role-select" className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                    Default role:
                  </Label>
                  <Select value={addUserRole} onValueChange={(val) => setAddUserRole(val as 'editor' | 'viewer')}>
                    <SelectTrigger id="role-select" className="w-[130px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">
                        <div className="flex items-center gap-2">
                          <Eye className="h-3.5 w-3.5" />
                          Viewer
                        </div>
                      </SelectItem>
                      <SelectItem value="editor">
                        <div className="flex items-center gap-2">
                          <Shield className="h-3.5 w-3.5" />
                          Editor
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by username or name..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
                
                {addUserError && (
                  <div className="rounded-md bg-destructive/10 border border-destructive/20 p-2.5">
                    <p className="text-xs text-destructive font-medium">{addUserError}</p>
                  </div>
                )}
                
                {userSearchLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <Spinner className="h-6 w-6 text-muted-foreground" />
                  </div>
                ) : addableUsers.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
                    <Search className="h-6 w-6 mx-auto mb-2 text-muted-foreground opacity-50" />
                    <p className="text-xs text-muted-foreground">
                      {userSearchQuery.trim() 
                        ? 'No users found or already added' 
                        : 'Start typing to search for users'}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <div className="max-h-[240px] overflow-y-auto">
                      <div className="divide-y divide-border">
                        {addableUsers.map((u) => (
                          <div
                            key={u.id}
                            className="flex items-center justify-between p-3 hover:bg-accent/50 transition-colors group"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <Avatar className="h-9 w-9">
                                <AvatarFallback className="bg-muted text-xs font-medium">
                                  {getInitials(u.full_name, u.username)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">
                                  {u.full_name || u.username}
                                </p>
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              onClick={() => handleAddUser(u)}
                              className="h-8 px-3 text-xs"
                            >
                              <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter className="sm:justify-end">
            <Button onClick={handleAddUserClose} variant="outline">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit library</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="My Projects"
                className="mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description (optional)</Label>
              <Textarea
                id="edit-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="A brief description..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleEditClose}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={!editName.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
